import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AlertService } from 'src/core/services/alert.service';
import { BatchService } from 'src/core/services/batch.service';
import { Batch, PrintBarcodePayload, PrintLabelPayload } from 'src/interfaces/batch';
import { MedicineDosagePackageType } from 'src/interfaces/medicine-dosage-package-type';
import { environment } from "src/environments/environment";
import { UtilsService } from 'src/core/services/utils.service';
import { CompanyService } from 'src/core/services/company.service';
import { Company } from 'src/interfaces/company';

interface DialogData {
  title: string;
  message: string;
  printType: string;
  batch: Batch;
};

@Component({
    selector: 'core-batch-print-dialog',
    templateUrl: './batch-print-dialog.component.html',
    styleUrls: ['./batch-print-dialog.component.scss'],
    standalone: false
})
export class BatchPrintDialogComponent implements OnInit {
  progressValue: number = 0;
  nameControl = this._formBuilder.control('', Validators.required);

  printLabelsForm = this._formBuilder.group({
    client: ['', Validators.required],
    prescriber: ['', Validators.required],
    size: [''],
  })
  batch: Batch | null = null;
  selectedClient: Company | null = null;
  selectedPrescriber: Company | null = null;
  largeSize: boolean = false;
  constructor(
    public _dialogRef: MatDialogRef<BatchPrintDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private _batchService: BatchService,
    private _formBuilder: UntypedFormBuilder,
    private _alertService: AlertService,
    private _utilService: UtilsService,
    private _companyService: CompanyService
  ) { }

  ngOnInit(): void {
    this.batch = this.data.batch;
  }

  async print() {
    await this.printBarcodes();
  }


  async printBarcodes() {

    if (this.data.batch.expiry_date) {
      const expiryDate: Date = new Date(this.data.batch.expiry_date);

      if (!expiryDate) {
        this._alertService
          .warningAlert("Please select an expiry date");
        return;
      }


      const medicineDosagePackageType: MedicineDosagePackageType | null = this.batch ? (this.batch
        .medicine_dosage_type
        .medicine_dosage_package_type.length > 0 ? this.batch
          .medicine_dosage_type
          .medicine_dosage_package_type.filter(type =>  type.id === this.batch?.medicine_dosage_package_type_id)[0] : null)
        : null;

      if (!medicineDosagePackageType) {
        this._alertService.errorAlert("Invalid package type");
        return;
      }

      const dateStr: string = `${expiryDate.getFullYear()}/${expiryDate.getMonth() + 1}/${expiryDate.getDate()}`;

      const payload: PrintBarcodePayload = {
        description: medicineDosagePackageType.label_name,
        expiry_date: dateStr,
        batch_number: this.data.batch.batch_number,
        quantity: this.data.batch.quantity_produced,
        starting_number: 1
      }

      const success = await this._batchService
        .printBarcode(payload);

      if (success) this._dialogRef.close(success);
    } else {
      this._alertService
        .warningAlert("Your batch does not have an expiry date. Please edit your batch to have an expiry date.");
    }

  }

  async openSearchClientDialog() {
    this.selectedClient = await this._companyService
      .openCompanySearchDialog("Search for a client/company");

    if (!this.selectedClient) return;

    this
      .printLabelsForm
      .patchValue({
        client: this.selectedClient ? this.selectedClient.name : null
      });

  }

  deselectClient() {
    this.selectedClient = null;
    this
      .printLabelsForm
      .patchValue({
        client: null
      });
  }

  async openSearchPrescriberDialog() {
    this.selectedPrescriber = await this._companyService
      .openCompanySearchDialog("Search for a prescriber/company");

    if (!this.selectedPrescriber) return;
    this
      .printLabelsForm
      .patchValue({
        prescriber: this.selectedPrescriber ? this.selectedPrescriber.name : null
      });

  }

  deselectPrescriber() {
    this.selectedPrescriber = null;
    this
      .printLabelsForm
      .patchValue({
        prescriber: null
      });
  }

}
